import { useLessonReviewMode } from '../hooks/useLessonReviewMode';
import React, { useMemo, useEffect, useState } from 'react';
import { useAssessmentContext } from '../contexts/AssessmentContext';
import { formatDate, getAssessmentTypeName } from '../utils/assessmentUtils';
import { ASSESSMENT_KINDS } from '../db/schema';
import { api } from '../api/client';
import InstructionAccess from './InstructionAccess';
import { assessmentIdOf } from '../utils/assessmentIdentity';
import { LogOut, CheckCircle, Award, Star, Trophy, Zap, Sparkles, ArrowRightCircle, Coffee, ListChecks } from 'lucide-react';

/* ────────────────────────────────────────────────────────── */
/*  Helper utilities                                         */
/* ────────────────────────────────────────────────────────── */

const formatAssessmentName = (name = '') =>
  name.toLowerCase() === 'kw8readingcomprehension'
    ? 'KW8 Reading Comprehension'
    : name.replace(/\b\w/g, c => c.toUpperCase());

const formatStudentNameForDisplay = (originalName = '', gradeLevel = '', accessCode = '') => {
  if (!originalName || typeof originalName !== 'string') return 'Student';
  
  const trimmed = originalName.trim();
  if (!trimmed) return 'Student';
  
  if (trimmed.includes('***')) {
    return trimmed;
  }
  
  try {
    const cleanName = trimmed.toUpperCase();
    if (cleanName.length === 0) {
      return 'Student';
    }

    const firstLetter = cleanName.charAt(0);
    const lastLetter = cleanName.charAt(cleanName.length - 1);
    const gradeNumber = gradeLevel ? gradeLevel.toString().replace(/[^0-9]/g, '') : '0';
    
    let subUserCode = '000';
    if (accessCode && accessCode.includes('-')) {
      const parts = accessCode.split('-');
      if (parts.length > 1) {
        const suffix = parts[1].padStart(3, '0');
        subUserCode = suffix.substring(0, 3);
      }
    }

    const formattedName = `${firstLetter}***${gradeNumber}${subUserCode}${lastLetter}`;
    return formattedName;
  } catch (error) {
    console.error('Error formatting student name for display:', error);
    return 'Student';
  }
};

/* ────────────────────────────────────────────────────────── */
/*  Component                                                */
/* ────────────────────────────────────────────────────────── */

export default function AssessmentComplete() {
  /* ========= context ========= */
  const {
    studentName,
    studentGrade,
    assessmentType,
    studentResponses,
    correctAnswers,
    resetAssessment,
    sessionId,
    assessmentData,
    accessCode,
    selectedAssessment,
    currentPerformanceId,
    prepareAnotherAssessment,
  } = useAssessmentContext();

  /* ========= animation states ========= */
  const [animationStage, setAnimationStage] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showBurstConfetti, setShowBurstConfetti] = useState(false);
  const [showExplodeConfetti, setShowExplodeConfetti] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [scoreAnimated, setScoreAnimated] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionError, setTransitionError] = useState('');
  const [sequenceStatus, setSequenceStatus] = useState(null);
  const [sequenceLoading, setSequenceLoading] = useState(true);
  const reviewMode=useLessonReviewMode(Boolean(accessCode),accessCode);
  const [sequenceError, setSequenceError] = useState('');

  /* ========= enhanced style injection ========= */
  useEffect(() => {
    if (!document.getElementById('assessment-complete-styles')) {
      const css = document.createElement('style');
      css.id = 'assessment-complete-styles';
      css.textContent = `
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(30px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideInLeft {
          0% { opacity: 0; transform: translateX(-50px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes slideInRight {
          0% { opacity: 0; transform: translateX(50px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes scaleIn {
          0% { opacity: 0; transform: scale(0.5); }
          50% { transform: scale(1.1); }
          100% { opacity: 1; transform: scale(1); }
        }
        
        @keyframes growBar {
          0% { width: 0%; }
          100% { width: var(--score-width); }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% { transform: translateY(0); }
          40%, 43% { transform: translateY(-10px); }
          70% { transform: translateY(-5px); }
          90% { transform: translateY(-2px); }
        }
        
        @keyframes sparkle {
          0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
          50% { opacity: 1; transform: scale(1) rotate(180deg); }
        }
        
        @keyframes confettiFall {
          0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
        }
        
        @keyframes confettiBurst {
          0% { 
            transform: scale(0) translateY(0) rotate(0deg); 
            opacity: 1; 
          }
          20% { 
            transform: scale(1.2) translateY(-20px) rotate(90deg); 
            opacity: 1; 
          }
          100% { 
            transform: scale(0.8) translateY(100vh) rotate(360deg); 
            opacity: 0; 
          }
        }
        
        @keyframes confettiExplode {
          0% { 
            transform: scale(0) translate(0, 0) rotate(0deg); 
            opacity: 1; 
          }
          10% { 
            transform: scale(1.5) translate(var(--x-offset), var(--y-offset)) rotate(180deg); 
            opacity: 1; 
          }
          100% { 
            transform: scale(0.5) translate(calc(var(--x-offset) * 3), calc(var(--y-offset) * 3 + 100vh)) rotate(720deg); 
            opacity: 0; 
          }
        }
        
        @keyframes celebrationPulse {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); opacity: 0.8; }
        }
        
        @keyframes colorShift {
          0% { color: #e2e8f0; text-shadow: 0 0 15px rgba(226, 232, 240, 0.4); }
          10% { color: #10B981; text-shadow: 0 0 20px rgba(16, 185, 129, 0.6); }
          20% { color: #3b82f6; text-shadow: 0 0 20px rgba(59, 130, 246, 0.6); }
          30% { color: #f59e0b; text-shadow: 0 0 20px rgba(245, 158, 11, 0.6); }
          40% { color: #ef4444; text-shadow: 0 0 20px rgba(239, 68, 68, 0.6); }
          50% { color: #8b5cf6; text-shadow: 0 0 20px rgba(139, 92, 246, 0.6); }
          60% { color: #06b6d4; text-shadow: 0 0 20px rgba(6, 182, 212, 0.6); }
          70% { color: #84cc16; text-shadow: 0 0 20px rgba(132, 204, 22, 0.6); }
          80% { color: #f97316; text-shadow: 0 0 20px rgba(249, 115, 22, 0.6); }
          90% { color: #ec4899; text-shadow: 0 0 20px rgba(236, 72, 153, 0.6); }
          100% { color: #e2e8f0; text-shadow: 0 0 15px rgba(226, 232, 240, 0.4); }
        }
        
        .color-changing-title { color: #0f172a; }
        
        .stage-0 { animation: fadeInUp 0.8s ease-out; }
        .stage-1 { animation: slideInLeft 0.6s ease-out 0.3s both; }
        .stage-2 { animation: slideInRight 0.6s ease-out 0.6s both; }
        .stage-3 { animation: scaleIn 0.8s ease-out 0.9s both; }
        .stage-4 { animation: bounce 1s ease-out 1.2s both; }
        
        .confetti-piece {
          position: absolute;
          width: 8px;
          height: 8px;
          animation: confettiFall 3s linear infinite;
          pointer-events: none;
          z-index: 15;
        }
        
        .confetti-burst {
          position: absolute;
          width: 12px;
          height: 12px;
          animation: confettiBurst 2.5s ease-out infinite;
          pointer-events: none;
          z-index: 15;
        }
        
        .confetti-explode {
          position: absolute;
          width: 10px;
          height: 10px;
          animation: confettiExplode 3s ease-out forwards;
          pointer-events: none;
          z-index: 15;
        }
        
        .celebration-icon {
          position: absolute;
          animation: celebrationPulse 2s ease-in-out infinite;
          pointer-events: none;
          z-index: 12;
        }
        
        .sparkle-icon {
          position: absolute;
          animation: sparkle 1.5s ease-in-out infinite;
          pointer-events: none;
        }
        
        .score-counter {
          font-size: clamp(3rem, 9vw, 4.5rem);
          line-height: 1;
          font-weight: 800;
          text-align: center;
          margin: 0.85rem 0 1rem;
          letter-spacing: -0.06em;
        }
        
        .excellent-score {
          animation: pulse 1s ease-in-out infinite;
        }
        
        .exit-button:hover {
          background: #1d4ed8!important;
          transform: translateY(-2px)!important;
          box-shadow: 0 10px 24px rgba(37, 99, 235, 0.25)!important;
        }

        @keyframes backgroundFloat {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          35% { transform: translate3d(2.5rem, -1.5rem, 0) scale(1.08); }
          70% { transform: translate3d(-1.5rem, 2rem, 0) scale(0.94); }
        }

        .exit-button:focus-visible { outline: 3px solid #93c5fd; outline-offset: 3px; }
        .take-another-button:hover:not(:disabled) {
          background: #047857!important;
          transform: translateY(-2px)!important;
          box-shadow: 0 12px 28px rgba(5, 150, 105, 0.30)!important;
        }
        .take-another-button:focus-visible { outline: 3px solid #6ee7b7; outline-offset: 3px; }

        .completion-layout { display: grid!important; grid-template-columns: minmax(0, 1fr) minmax(280px, 31%); }
        .completion-main { min-width: 0; }
        .completion-actions { min-width: 0; }
        @media (max-width: 800px) {
          .completion-layout { display: flex!important; flex-direction: column; }
          .completion-main { flex: 1 1 auto; min-height: 0; }
          .completion-actions { flex-shrink: 0; border-left: 0!important; border-top: 1px solid #dbe3ee; box-shadow: 0 -8px 22px rgba(15, 23, 42, 0.05); padding: .85rem 1.25rem max(.85rem, env(safe-area-inset-bottom))!important; gap: .65rem!important; }
          .completion-actions-heading, .completion-actions-description { display: none; }
        }
        @media (max-height: 560px) and (max-width: 800px) {
          .completion-layout { max-height: calc(100dvh - 1rem)!important; }
          .completion-actions { padding-top: .65rem!important; padding-bottom: max(.65rem, env(safe-area-inset-bottom))!important; }
          .completion-actions .take-another-button { min-height: 52px!important; }
        }
        @media print {
          .completion-layout { display: block!important; max-height: none!important; overflow: visible!important; box-shadow: none!important; }
          .completion-main { overflow: visible!important; max-height: none!important; }
          .completion-actions { display: none!important; }
        }

        @media (max-width: 640px) {
          #info-box { margin: 0.75rem!important; }
          #results-box { margin: 0 0.75rem 0.75rem!important; }
          .info-label, .info-value { font-size: 0.84rem!important; }
        }

        @media (prefers-reduced-motion: reduce) {
          .stage-0, .stage-1, .stage-2, .stage-3, .stage-4, .excellent-score { animation: none!important; }
          .background-blob { animation: none!important; }
          #score-bar { transition: none!important; }
        }
        
        .floating-elements {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          overflow: hidden;
          z-index: 10;
        }
      `;
      document.head.appendChild(css);
    }
  }, []);

  /* ========= animation sequence ========= */
  useEffect(() => {
    const sequence = [
      { stage: 1, delay: 500 },
      { stage: 2, delay: 800 },
      { stage: 3, delay: 1100 },
      { stage: 4, delay: 1400 }
    ];

    sequence.forEach(({ stage, delay }) => {
      setTimeout(() => setAnimationStage(stage), delay);
    });

    // Trigger multiple confetti effects for high scores
    setTimeout(() => {
      if (scorePercentage >= 80) {
        // Start with explosion confetti from center
        setShowExplodeConfetti(true);
        
        // Then burst confetti from multiple points
        setTimeout(() => setShowBurstConfetti(true), 300);
        
        // Finally falling confetti
        setTimeout(() => setShowConfetti(true), 600);
        
        // Show celebration icons
        setShowCelebration(true);
        
        // Stop confetti effects
        setTimeout(() => setShowExplodeConfetti(false), 3500);
        setTimeout(() => setShowBurstConfetti(false), 4000);
        setTimeout(() => setShowConfetti(false), 5000);
      } else if (scorePercentage >= 60) {
        // Medium score - just burst and fall
        setShowBurstConfetti(true);
        setTimeout(() => setShowConfetti(true), 400);
        setTimeout(() => setShowBurstConfetti(false), 3000);
        setTimeout(() => setShowConfetti(false), 4000);
      } else if (scorePercentage >= 40) {
        // Lower score - just falling confetti
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }
    }, 1500);

    // Animate score counter
    setTimeout(() => setScoreAnimated(true), 1200);
  }, []);

  /* ========= derived data ========= */
  const formattedStudentName = useMemo(() => 
    formatStudentNameForDisplay(studentName, studentGrade, accessCode), 
    [studentName, studentGrade, accessCode]
  );

  const [assessmentInfo, setAssessmentInfo] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let timerId;
    const loadSequence = async () => {
      try {
        const status = await api.getStudentAssessmentSequenceStatus({
          learnerAlias: studentName,
          actualGradeLevel: studentGrade,
          signal: controller.signal,
        });
        setSequenceStatus(status);
        setSequenceError('');
      } catch (error) {
        if (error?.name === 'AbortError') return;
        setSequenceError('Today’s assessment count could not be refreshed. The server will verify the limit before another assessment starts.');
      } finally {
        if (!controller.signal.aborted) setSequenceLoading(false);
      }
    };
    timerId = window.setTimeout(loadSequence, 250);
    return () => {
      window.clearTimeout(timerId);
      controller.abort();
    };
  }, [studentGrade, studentName]);

  const totalQuestions = useMemo(
    () => assessmentData?.Grades?.[0]?.Questions?.length || 0,
    [assessmentData]
  );

  const scorePercentage = useMemo(() => {
    if (!assessmentData?.Grades?.[0]?.Questions) return 0;

    if (correctAnswers && Object.keys(correctAnswers).length) {
      const correctCount = assessmentData.Grades[0].Questions.filter(q => {
        const idStr = String(q.ItemNumber);
        return !!(correctAnswers[idStr] ?? correctAnswers[q.ItemNumber]);
      }).length;
      return totalQuestions ? Math.round((correctCount / totalQuestions) * 100) : 0;
    }

    const correctCount = Object.values(studentResponses || {}).filter(r => {
      if (typeof r === 'string') return false;
      return (
        r.isCorrect === true ||
        r.isCorrect === 'true' ||
        r.isCorrect === 1 ||
        r.is_correct === true ||
        r.is_correct === 'true' ||
        r.is_correct === 1
      );
    }).length;
    return totalQuestions ? Math.round((correctCount / totalQuestions) * 100) : 0;
  }, [assessmentData, correctAnswers, studentResponses, totalQuestions]);

  const displayAssessmentName = useMemo(() => {
    if (selectedAssessment && selectedAssessment.name) {
      return selectedAssessment.name;
    }
    
    if (!assessmentType) return '';
    return typeof assessmentType === 'object' && assessmentType.name
      ? formatAssessmentName(assessmentType.name)
      : formatAssessmentName(getAssessmentTypeName(assessmentType));
  }, [selectedAssessment, assessmentType]);

  /* ========= assessment info loading ========= */
  useEffect(() => {
    const resolveInfo = async () => {
      if (selectedAssessment) {
        setAssessmentInfo({
          id: selectedAssessment.id || assessmentType,
          name: selectedAssessment.name || displayAssessmentName,
          assessmentKind: selectedAssessment.assessmentKind || assessmentData?.assessmentKind
        });
        return;
      }
      
      if (assessmentData?.assessmentKind) {
        setAssessmentInfo({
          id: assessmentData.id || assessmentType,
          name: assessmentData.name || displayAssessmentName,
          assessmentKind: assessmentData.assessmentKind
        });
        return;
      }

      try {
        const assessmentId = assessmentIdOf(assessmentType);
        if (assessmentId) {
          const match = await api.getAssessment(assessmentId);
          if (match) {
            setAssessmentInfo(match);
            return;
          }
        }
      } catch (err) {
        console.error('Assessment lookup failed:', err);
      }

      const kind = (() => {
        const t = typeof assessmentType === 'string' ? assessmentType.toLowerCase() : '';
        if (t.match(/reading|calculation|problem\-solving|phonics|spelling|vocabulary|written-language/)) return 'gradeLevel';
        if (t.includes('solar-system') || t.startsWith('rc-obj')) return 'regular';
        return t.includes('grade') ? 'gradeLevel' : 'regular';
      })();

      setAssessmentInfo({
        id: typeof assessmentType === 'string' ? assessmentType : 'unknown',
        name: displayAssessmentName,
        assessmentKind: kind
      });
    };
    resolveInfo();
  }, [assessmentType, assessmentData, displayAssessmentName, selectedAssessment]);

  const isRegularAssessment = assessmentInfo?.assessmentKind === 'regular';

  /* ========= utility functions ========= */
  const getCorrectAnswerCount = () => {
    if (!assessmentData?.Grades?.[0]?.Questions) return 0;
    return assessmentData.Grades[0].Questions.filter(q => {
      const idStr = String(q.ItemNumber);
      return correctAnswers?.[idStr] === true || correctAnswers?.[q.ItemNumber] === true;
    }).length;
  };

  const getPerformanceMessage = () => {
    if (!isRegularAssessment) return 'Your assessment has been completed successfully.';
    if (scorePercentage >= 90) return "Excellent work! You've mastered this assessment.";
    if (scorePercentage >= 70) return 'Good job! You\'ve shown a solid understanding.';
    if (scorePercentage >= 50) return "You've completed the assessment with some understanding. More practice will help!";
    return "You've completed the assessment. More study and practice will help improve your understanding.";
  };

  const getScoreBarColor = s => (s >= 90 ? '#10B981' : s >= 70 ? '#0EA5E9' : s >= 50 ? '#F59E0B' : '#EF4444');

  const getPerformanceIcon = () => {
    if (scorePercentage >= 90) return <Trophy size={26} style={{ color: '#FFD700' }} />;
    if (scorePercentage >= 70) return <Award size={26} style={{ color: '#10B981' }} />;
    if (scorePercentage >= 50) return <Star size={26} style={{ color: '#64748b' }} />;
    return <Zap size={26} style={{ color: '#94a3b8' }} />;
  };

  /* ========= animated score counter ========= */
  const AnimatedScore = ({ targetScore }) => {
    const [currentScore, setCurrentScore] = useState(0);

    useEffect(() => {
      if (!scoreAnimated) return;

      let start = 0;
      const increment = targetScore / 50;
      const timer = setInterval(() => {
        start += increment;
        if (start >= targetScore) {
          setCurrentScore(targetScore);
          clearInterval(timer);
        } else {
          setCurrentScore(Math.floor(start));
        }
      }, 40);

      return () => clearInterval(timer);
    }, [targetScore, scoreAnimated]);

    return (
      <div className={`score-counter ${targetScore >= 90 ? 'excellent-score' : ''}`}>
        {currentScore}%
      </div>
    );
  };

  /* ========= confetti components ========= */
  const ConfettiPiece = ({ delay, leftPosition }) => (
    <div
      className="confetti-piece"
      style={{
        left: `${leftPosition}%`,
        animationDelay: `${delay}s`,
        background: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3', '#54a0ff'][Math.floor(Math.random() * 7)]
      }}
    />
  );

  const BurstConfetti = ({ delay, leftPosition, topPosition }) => (
    <div
      className="confetti-burst"
      style={{
        left: `${leftPosition}%`,
        top: `${topPosition}%`,
        animationDelay: `${delay}s`,
        background: ['#10B981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'][Math.floor(Math.random() * 6)]
      }}
    />
  );

  const ExplodeConfetti = ({ index }) => {
    const angle = (index * 360) / 24; // 24 pieces in circle
    const distance = 50 + Math.random() * 100;
    const xOffset = Math.cos(angle * Math.PI / 180) * distance;
    const yOffset = Math.sin(angle * Math.PI / 180) * distance;
    
    return (
      <div
        className="confetti-explode"
        style={{
          left: '50%',
          top: '50%',
          '--x-offset': `${xOffset}px`,
          '--y-offset': `${yOffset}px`,
          animationDelay: `${Math.random() * 0.5}s`,
          background: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3', '#54a0ff', '#10B981'][Math.floor(Math.random() * 8)]
        }}
      />
    );
  };

  /* ========= event handlers ========= */
  const handlePrint = () => {
    const style = document.createElement('style');
    style.textContent = `@media print{body*{visibility:hidden}#printable-content,#printable-content *{visibility:visible}#printable-content{position:absolute;left:0;top:0;width:100%;padding:2cm;background:#fff;color:#1e293b;overflow:visible!important;max-height:none!important}#score-bar{background:${getScoreBarColor(scorePercentage)}!important}#button-container{display:none!important}@page{margin:2cm}}`;
    document.head.appendChild(style);
    window.print();
    document.head.removeChild(style);
  };

  const handleExit = () => {
    resetAssessment();
    window.location.href = '/';
  };

  const handleTakeAnotherAssessment = () => {
    if (isTransitioning) return;
    if (sequenceStatus && !sequenceStatus.canTakeAnother) {
      setTransitionError(sequenceStatus.limitReached
        ? `You completed today’s limit of ${sequenceStatus.dailyLimit} assessments. Please check with your teacher.`
        : 'Another assessment is not enabled for today. Please check with your teacher.');
      return;
    }
    setIsTransitioning(true);
    setTransitionError('');
    try {
      prepareAnotherAssessment();
    } catch (error) {
      setTransitionError(error?.message || 'The next assessment could not be prepared. Please try again.');
      setIsTransitioning(false);
    }
  };

  /* ========= loading state ========= */
  if (!assessmentInfo) {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.card, padding: '2rem', textAlign: 'center' }}>
          <div style={{ color: '#E2E8F0', fontSize: 18, animation: 'pulse 1s ease-in-out infinite' }}>
            Loading assessment information…
          </div>
        </div>
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────── */
  /*  Render                                                 */
  /* ─────────────────────────────────────────────────────── */

  return (
    <div style={styles.container} id="main-container">
      {/* Enhanced decorative elements */}
      <div style={styles.blobs}>
        <div className="background-blob" style={{ ...styles.blob, top: '-7rem', left: '-5rem', background: 'rgba(59, 130, 246, 0.20)' }} />
        <div className="background-blob" style={{ ...styles.blob, bottom: '-8rem', right: '-5rem', background: 'rgba(20, 184, 166, 0.16)', animationDelay: '-3s' }} />
        <div className="background-blob" style={{ ...styles.blob, top: '34%', right: '4%', background: 'rgba(139, 92, 246, 0.13)', animationDelay: '-6s' }} />
      </div>

      {/* Floating celebration elements */}
      <div className="floating-elements">
        {/* Exploding confetti from center */}
        {showExplodeConfetti && Array.from({ length: 24 }).map((_, i) => (
          <ExplodeConfetti key={`explode-${i}`} index={i} />
        ))}
        
        {/* Bursting confetti from multiple points */}
        {showBurstConfetti && Array.from({ length: 30 }).map((_, i) => (
          <BurstConfetti
            key={`burst-${i}`}
            delay={Math.random() * 2}
            leftPosition={Math.random() * 100}
            topPosition={Math.random() * 60}
          />
        ))}
        
        {/* Traditional falling confetti */}
        {showConfetti && Array.from({ length: 50 }).map((_, i) => (
          <ConfettiPiece
            key={`fall-${i}`}
            delay={Math.random() * 3}
            leftPosition={Math.random() * 100}
          />
        ))}
        
        {/* Celebration icons */}
        {showCelebration && (
          <>
            <div className="celebration-icon" style={{ top: '20%', left: '15%' }}>
              <Star size={24} style={{ color: '#FFD700' }} />
            </div>
            <div className="celebration-icon" style={{ top: '30%', right: '15%' }}>
              <Sparkles size={28} style={{ color: '#ff9ff3' }} />
            </div>
            <div className="celebration-icon" style={{ bottom: '25%', left: '20%' }}>
              <Trophy size={32} style={{ color: '#10B981' }} />
            </div>
            <div className="celebration-icon" style={{ top: '40%', left: '10%' }}>
              <Award size={26} style={{ color: '#3b82f6' }} />
            </div>
            <div className="celebration-icon" style={{ bottom: '35%', right: '20%' }}>
              <Star size={20} style={{ color: '#f59e0b' }} />
            </div>
          </>
        )}
      </div>

      <div style={styles.card} className="stage-0 completion-layout">
        <div id="printable-content" className="completion-main" style={styles.scrollContent}>
          {/* Animated header */}
          <header style={styles.header} className={animationStage >= 1 ? 'stage-1' : ''}>
            <h2 style={styles.headerTitle} className="color-changing-title">
              <span style={styles.completionIcon}><CheckCircle size={24} /></span>
              Assessment Complete!
            </h2>
            <div style={{ marginTop: '0.5rem', fontSize: '1.25rem' }}>
              {getPerformanceIcon()}
            </div>
          </header>

          {/* Animated info box */}
          <section 
            style={styles.infoBox} 
            className={animationStage >= 2 ? 'stage-2' : ''}
            id="info-box"
          >
            <InfoRow label="Student:" value={formattedStudentName} />
            <InfoRow label="Assessment:" value={displayAssessmentName} />
            <InfoRow
              label="Assessment Type:"
              value={isRegularAssessment ? 'Regular Assessment' : 'Grade Level Assessment'}
              noMb
            />
          </section>

          {/* Animated results */}
          <section 
            style={styles.resultsBox} 
            className={animationStage >= 3 ? 'stage-3' : ''}
            id="results-box"
          >
            <h3 style={styles.resultsHeader}>
              <Award size={20} /> Results
            </h3>

            {isRegularAssessment ? (
              <>
                <p style={styles.eyebrow}>FINAL SCORE</p>
                <div style={{ color: getScoreBarColor(scorePercentage) }}>
                  <AnimatedScore targetScore={scorePercentage} />
                </div>

                <div style={styles.scoreBarWrap} className={animationStage >= 4 ? 'stage-4' : ''}>
                  <div
                    id="score-bar"
                    style={{
                      ...styles.scoreBar,
                      width: scoreAnimated ? `${scorePercentage}%` : '0%',
                      backgroundColor: getScoreBarColor(scorePercentage),
                      '--score-width': `${scorePercentage}%`,
                      transition: 'width 2s ease-out',
                      boxShadow: `0 0 20px ${getScoreBarColor(scorePercentage)}40`
                    }}
                  />
                </div>

                <p style={styles.scoreDisplay}>
                  <strong>{getCorrectAnswerCount()}</strong> of <strong>{totalQuestions}</strong> answers correct
                </p>

                <p style={styles.performanceMsg}>{getPerformanceMessage()}</p>
              </>
            ) : (
              <>
                <p style={styles.scoreDisplay}>
                  Your Score: <strong>To Be Processed</strong>
                </p>
                <p style={styles.performanceMsg}>Your assessment has been completed successfully.</p>
              </>
            )}
          </section>

          <section style={styles.sequenceCard} aria-live="polite">
            <div style={styles.sequenceHeading}>
              <ListChecks size={20} aria-hidden="true" /> Today’s Assessment Plan
            </div>
            {sequenceLoading ? (
              <p style={styles.sequenceText}>Checking today’s completed assessments…</p>
            ) : sequenceStatus ? (
              <>
                <p style={styles.sequenceText}>
                  <strong>{sequenceStatus.completedToday} of {sequenceStatus.dailyLimit}</strong> assessments completed today
                  {sequenceStatus.remainingToday > 0
                    ? ` • ${sequenceStatus.remainingToday} remaining`
                    : ' • daily limit reached'}
                </p>
                {sequenceStatus.breakMinutes > 0 && sequenceStatus.canTakeAnother && (
                  <p style={styles.breakText}>
                    <Coffee size={17} aria-hidden="true" /> Take a {sequenceStatus.breakMinutes}-minute break before beginning the next assessment.
                  </p>
                )}
                {sequenceStatus.history?.length > 0 && (
                  <p style={styles.historyText}>
                    Completed today: {sequenceStatus.history.map(item => item.assessmentName).join(' • ')}
                  </p>
                )}
              </>
            ) : (
              <p role="status" style={styles.sequenceWarning}>{sequenceError}</p>
            )}
          </section>
        </div>

        {/* Animated buttons */}
        <div 
          style={styles.buttons} 
          className="completion-actions"
          id="button-container"
        >
          <div className="completion-actions-heading" style={styles.actionsHeading}>What’s next?</div>
          <InstructionAccess phase={reviewMode.enabled?"before":"after"} assessmentId={assessmentIdOf(selectedAssessment)} performanceId={currentPerformanceId} reviewOnly={reviewMode.enabled} />
          <p className="completion-actions-description" style={styles.actionsDescription}>
            Continue when you’re ready, or return to the assessment list.
          </p>
          {transitionError && (
            <div role="alert" style={styles.transitionError}>{transitionError}</div>
          )}
          <button
            type="button"
            style={{
              ...styles.btn,
              ...styles.takeAnother,
              opacity: isTransitioning || sequenceLoading || sequenceStatus?.canTakeAnother === false ? 0.65 : 1,
              background: sequenceStatus?.canTakeAnother === false ? '#64748b' : styles.takeAnother.background,
            }}
            onClick={reviewMode.enabled?()=>{window.dispatchEvent(new window.CustomEvent('assessnow-study-lessons-open',{detail:{assessmentId:assessmentIdOf(selectedAssessment)}}));}:handleTakeAnotherAssessment}
            className="take-another-button"
            disabled={!reviewMode.enabled&&(isTransitioning || sequenceLoading || sequenceStatus?.canTakeAnother === false)}
            aria-busy={isTransitioning}
          >
            <ArrowRightCircle size={26} aria-hidden="true" />
            {reviewMode.enabled?'Study my Lessons':isTransitioning
              ? 'Preparing Next Assessment…'
              : sequenceStatus?.limitReached
                ? 'Today’s Assessment Limit Reached'
                : sequenceStatus?.allowTakeAnother === false
                  ? 'Another Assessment Requires Teacher Approval'
                  : 'Take Another Assessment'}
          </button>
          <button 
            style={{ ...styles.btn, ...styles.exit }} 
            onClick={handleExit} 
            className="exit-button"
          >
            <LogOut size={18} /> Return to assessments
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */
/*  Small reusable components                                */
/* ────────────────────────────────────────────────────────── */

const InfoRow = ({ label, value, noMb }) => (
  <div style={{ ...styles.infoRow, marginBottom: noMb ? 0 : '1rem' }}>
    <span style={styles.infoLabel} className="info-label">{label}</span>
    <span style={styles.infoValue} className="info-value">{value}</span>
  </div>
);

const ScoreRow = ({ label, value }) => (
  <div style={styles.scoreRow}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

/* ────────────────────────────────────────────────────────── */
/*  Enhanced inline style map                               */
/* ────────────────────────────────────────────────────────── */

const styles = {
  container: {
    minHeight: '100vh',
    background: 'radial-gradient(circle at 50% 0%, #eff6ff 0%, #f8fafc 42%, #eef2f7 100%)',
    padding: 'clamp(1rem, 4vw, 2.5rem)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'auto'
  },
  blobs: { 
    position: 'absolute', 
    inset: 0, 
    overflow: 'hidden', 
    pointerEvents: 'none', 
    zIndex: 0 
  },
  blob: {
    position: 'absolute',
    width: '25rem',
    height: '25rem',
    backgroundColor: 'rgba(59, 130, 246, 0.16)',
    borderRadius: '50%',
    filter: 'blur(42px)',
    animation: 'backgroundFloat 10s ease-in-out infinite',
    willChange: 'transform'
  },
  card: {
    maxWidth: 1080,
    width: '100%',
    maxHeight: 'calc(100dvh - 2rem)',
    background: '#ffffff',
    borderRadius: '1.25rem',
    boxShadow: '0 24px 60px rgba(15, 23, 42, 0.10), 0 4px 14px rgba(15, 23, 42, 0.05)',
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    color: '#0f172a',
    zIndex: 5,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column'
  },
  scrollContent: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
  },
  header: {
    padding: '1.75rem 1.5rem 1.35rem',
    textAlign: 'center',
    borderBottom: '1px solid #e2e8f0',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
    flexShrink: 0
  },
  headerTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#0f172a',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    letterSpacing: '-0.02em'
  },
  completionIcon: {
    width: '2.5rem', height: '2.5rem', borderRadius: '999px', display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center', color: '#2563eb', background: '#dbeafe'
  },
  infoBox: {
    background: '#f8fafc',
    margin: '1.25rem 1.25rem 1rem',
    padding: '1.1rem 1.2rem',
    borderRadius: '0.75rem',
    border: '1px solid #e2e8f0',
    flexShrink: 0
  },
  infoRow: { 
    display: 'flex', 
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  infoLabel: { 
    color: '#64748b', 
    fontWeight: 'bold', 
    fontSize: '0.9rem',
    letterSpacing: '0.01em'
  },
  infoValue: { 
    color: '#0f172a', 
    fontSize: '0.9rem', 
    textAlign: 'right',
    fontWeight: '600',
    maxWidth: '65%'
  },
  resultsBox: {
    background: '#ffffff',
    margin: '0 1.25rem 1.25rem',
    padding: '1.35rem 1.2rem 1.5rem',
    borderRadius: '0.75rem',
    border: '1px solid #dbe3ee',
    textAlign: 'center',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  resultsHeader: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#334155',
    textAlign: 'center',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    marginTop: 0
  },
  eyebrow: {
    margin: '0.15rem 0 0', color: '#64748b', fontWeight: 800,
    fontSize: '0.75rem', letterSpacing: '0.12em'
  },
  scoreBarWrap: {
    width: '100%',
    height: 12,
    background: '#e2e8f0',
    borderRadius: 6,
    marginBottom: '1rem',
    overflow: 'hidden',
    border: '1px solid #dbe3ee'
  },
  scoreBar: {
    height: '100%',
    borderRadius: '6px',
    background: 'linear-gradient(90deg, #10B981, #059669)',
    position: 'relative',
    overflow: 'hidden'
  },
  scoreDisplay: { 
    fontSize: '1rem', 
    color: '#334155', 
    textAlign: 'center', 
    margin: '0.75rem 0',
    fontWeight: '600',
    textShadow: 'none'
  },
  performanceMsg: { 
    fontSize: '0.9rem', 
    color: '#64748b', 
    textAlign: 'center', 
    margin: '0.75rem 0',
    lineHeight: '1.5'
  },
  buttons: { 
    display: 'flex', 
    flexDirection: 'column',
    justifyContent: 'center', 
    alignItems: 'stretch',
    gap: '0.9rem', 
    padding: 'clamp(1.25rem, 3vw, 2rem)',
    flexShrink: 0,
    background: 'linear-gradient(160deg, #f8fbff 0%, #eef6f5 100%)',
    borderLeft: '1px solid #dbe3ee',
    overflowY: 'auto',
    zIndex: 16
  },
  actionsHeading: {
    fontSize: '1.35rem', fontWeight: 800, letterSpacing: '-0.025em', color: '#0f172a',
  },
  actionsDescription: {
    margin: '-.35rem 0 .7rem', color: '#475569', lineHeight: 1.5, fontSize: '.92rem',
  },
  btn: { 
    color: '#fff', 
    border: '1px solid #1d4ed8', 
    borderRadius: '0.7rem', 
    padding: '0.8rem 1.4rem', 
    fontSize: '1rem', 
    fontWeight: 'bold', 
    cursor: 'pointer', 
    transition: 'all .3s ease', 
    display: 'flex', 
    alignItems: 'center', 
    gap: '.4rem', 
    boxShadow: '0 6px 16px rgba(37, 99, 235, 0.18)',
    justifyContent: 'center',
    minWidth: 0,
    width: '100%',
    lineHeight: 1.25,
    textAlign: 'center',
    whiteSpace: 'normal'
  },
  exit: { 
    background: '#2563eb',
    position: 'relative',
    overflow: 'hidden'
  },
  takeAnother: {
    width: '100%',
    minHeight: '68px',
    background: '#059669',
    borderColor: '#047857',
    fontSize: '1.1rem',
    boxShadow: '0 8px 22px rgba(5, 150, 105, 0.24)',
  },
  sequenceCard: {
    margin: '0 1.25rem 1rem',
    padding: '1rem 1.1rem',
    border: '1px solid #bfdbfe',
    borderRadius: '.8rem',
    background: '#eff6ff',
    color: '#1e3a8a',
  },
  sequenceHeading: {
    display: 'flex',
    alignItems: 'center',
    gap: '.45rem',
    fontWeight: 800,
    marginBottom: '.45rem',
  },
  sequenceText: { margin: 0, fontSize: '.92rem', lineHeight: 1.45 },
  breakText: {
    display: 'flex', alignItems: 'center', gap: '.4rem', margin: '.55rem 0 0',
    color: '#065f46', fontWeight: 700, fontSize: '.88rem', lineHeight: 1.4,
  },
  historyText: { margin: '.5rem 0 0', color: '#475569', fontSize: '.8rem', lineHeight: 1.4 },
  sequenceWarning: { margin: 0, color: '#92400e', fontSize: '.86rem', lineHeight: 1.4 },
  transitionError: {
    padding: '.75rem 1rem',
    borderRadius: '.65rem',
    border: '1px solid #fecaca',
    background: '#fff1f2',
    color: '#991b1b',
    fontSize: '.9rem',
    lineHeight: 1.4,
  },
  scoreRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
    color: '#64748b',
    fontSize: '0.9rem'
  }
};
